import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// FHIR Client types
declare global {
  interface Window {
    FHIR: any;
  }
}

export interface PatientInfo {
  name?: string;
  id?: string;
  birthDate?: string;
}

export interface UserInfo {
  id?: string;
  name?: string;
  email?: string;
  resourceType?: string;
  rawData?: any;
}

interface FhirContextType {
  client: any;
  patient: PatientInfo | null;
  user: UserInfo | null;
  encounter: any;
  accessToken: string | null;
  idToken: string | null;
  fhirServerUrl: string | null;
  isReady: boolean;
  error: string | null;
}

const FhirContext = createContext<FhirContextType | undefined>(undefined);

export const FhirContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [client, setClient] = useState<any>(null);
  const [patient, setPatient] = useState<PatientInfo | null>(null);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [encounter, setEncounter] = useState<any>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [fhirServerUrl, setFhirServerUrl] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeSmart = async () => {
      try {
        if (window.FHIR) {
          // Try to initialize SMART client
          console.log('🔄 Attempting to initialize SMART on FHIR...');
          const fhirClient = await window.FHIR.oauth2.ready();
          
          // Extract both access_token and id_token from FHIR client
          const idTokenValue = fhirClient.state?.tokenResponse?.id_token || null;
          const fhirServerUrlValue = fhirClient.state?.serverUrl || null;
          
          setIdToken(idTokenValue);
          setFhirServerUrl(fhirServerUrlValue);
          
          console.log('🆔 ID token extracted:', idTokenValue ? 'Present' : 'Missing');
          console.log('🌐 FHIR Server URL:', fhirServerUrlValue);
          
          // Log granted scopes for debugging
          const grantedScopes = fhirClient.state?.tokenResponse?.scope || 'No scopes info';
          console.log('🔐 Granted scopes:', grantedScopes);
          
          let patientInfo: PatientInfo | null = null;
          if (fhirClient.patient && fhirClient.patient.id) {
            try {
              // Try to get patient ID from the context first
              const patientId = fhirClient.patient.id;
              console.log('📋 Patient ID from context:', patientId);
              
              
              patientInfo = {
                id: patientId,
              };
            } catch (err) {
              console.error('Could not fetch patient data:', err);
              // Still set patient info with just the ID from context
              if (fhirClient.patient.id) {
                patientInfo = {
                  id: fhirClient.patient.id,
                  
                };
                console.warn('⚠️ Using patient ID from context without full patient data');
              }
            }
          }

          // Fetch user data similar to patient
          let userData: UserInfo | null = null;
          try {
            if (fhirClient.user && fhirClient.user.id) {
              const userResource = await fhirClient.user.read();
              // Extract user name with prefix
              let userName = 'Unknown User';
              if (userResource.name?.[0]) {
                const nameData = userResource.name[0];
                const prefix = nameData.prefix?.join(' ') || '';
                const given = nameData.given?.join(' ') || '';
                const family = nameData.family || '';
                
                // Build full name with prefix
                userName = [prefix, given, family]
                  .filter(part => part.length > 0)
                  .join(' ')
                  .trim();
              }
              
              // Extract user email from telecom
              const userEmail = userResource.telecom?.find((t: any) => t.system === 'email')?.value || undefined;
              
              userData = {
                id: userResource.id,
                name: userName,
                email: userEmail,
                resourceType: userResource.resourceType,
                rawData: userResource
              };
              
              console.log('✅ User data fetched:', {
                id: userData.id,
                name: userData.name,
                email: userData.email,
                resourceType: userData.resourceType
              });
            } else {
              console.warn('⚠️ No user context available');
            }
          } catch (err) {
            console.warn('Could not fetch user data:', err);
          }

          setClient(fhirClient);
          setPatient(patientInfo);
          setUser(userData);
          setEncounter(fhirClient.encounter);
          setIsReady(true);
          
          console.log('✅ SMART on FHIR initialized successfully');
        } else {
          setError('FHIR client library not loaded');
          setIsReady(true);
        }
      } catch (err: any) {
        console.error('SMART initialization error:', err);
        // Check if this is because we're not in a SMART launch context
        if (err.message && err.message.includes('No state')) {
          setError('FHIR protocol not available. Please launch this application through a SMART on FHIR enabled EHR system.');
        } else {
          setError(`SMART initialization failed: ${err.message || err}`);
        }
        setIsReady(true);
      }
    };

    if (!window.FHIR) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/fhirclient/build/fhir-client.js';
      script.onload = initializeSmart;
      script.onerror = () => {
        setError('Failed to load FHIR client library');
        setIsReady(true);
      };
      document.head.appendChild(script);
    } else {
      initializeSmart();
    }
  }, []);

  const value: FhirContextType = {
    client,
    patient,
    user,
    encounter,
    accessToken,
    idToken,
    fhirServerUrl,
    isReady,
    error
  };

  return (
    <FhirContext.Provider value={value}>
      {children}
    </FhirContext.Provider>
  );
};

export const useFhirContext = () => {
  const context = useContext(FhirContext);
  if (context === undefined) {
    throw new Error('useFhir must be used within a FhirProvider');
  }
  return context;
};
